import { useEffect, useState } from "react";
import {
  BaSeGrid,
  BaSeGridItem,
  BaSeParagraph,
  useDebounce,
} from "@base/react";
import Deadlines from "../deadlines";
import SumWatcher from "../../../../components/form-builder/sum-watch";
import { colorsProgressBar } from "../functions";
import InputPercentageGraph from "../../../../components/form-builder/input-percentage-graph";
import AccordionModal from "../../../../components/accordion-modal";
import salesIcon from "../../../../assets/images/icons/sales-ops.svg";
import { sortedArrayByObjectKeys } from "../../../../services/utils";

const ReceiptDeadline = ({ data = [], isDisabled, onChange, showAccordion }) => {
  const [deadlines, setDeadlines] = useState([]);
  const [selectDeadlines, setSelectDeadlines] = useState([]);
  const [sum, setSum] = useState(undefined);
  const debouncedDeadlines = useDebounce(deadlines, 1000);

  useEffect(() => {
    if (data.length) {
      const sorted = sortedArrayByObjectKeys(data);
      setDeadlines(sorted);
      setSelectDeadlines(sorted.map((d) => d.order));
    }
  }, [data]);

  const toggleDeadline = (order) => {
    setSelectDeadlines((prev) =>
      prev.includes(order) ? prev.filter((o) => o !== order) : [...prev, order]
    );

    setDeadlines((prev) => {
      const exists = prev.some((item) => item.order === order);
      const updated = exists
        ? prev.filter((item) => item.order !== order)
        : [...prev, { order, value: 0 }];
      return sortedArrayByObjectKeys([...updated]);
    });
  };
  const handleChange = (order, newValue) => {
    setDeadlines((prev) => {
      const updated = prev.map((item) =>
        item.order === order ? { ...item, value: newValue } : item
      );
      return sortedArrayByObjectKeys([...updated]);
    });
  };

  useEffect(() => {
    const currentSum = deadlines
      .filter((item) => selectDeadlines.includes(item.order))
      .reduce((acc, item) => acc + Number(item.value || 0), 0);
    setSum(currentSum);
  }, [deadlines, selectDeadlines]);
  useEffect(() => {
    if (onChange && !isDisabled) {
      if (sum !== undefined) {
        onChange(deadlines);
      }
    }

    if (showAccordion) {
      showAccordion(sum === 100);
    }
    // eslint-disable-next-line
  }, [sum, debouncedDeadlines]);

  return (
    <div translate="no" className="step receipt-deadline">
      <div className="step--info">
        <h3 className="step--title">
          Selecione os prazos que deseja e preencha com os percentuais de Vendas
          para cada modalidade de recebimento, conforme acordados com seus
          clientes.
        </h3>
        <h5 className="step--subtitle icon">
          <span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10.0002 13.3327H10.0069M10.0002 8.33266V10.8327M8.81271 4.34682L2.93104 14.166C2.80986 14.376 2.74576 14.6141 2.74512 14.8566C2.74448 15.0991 2.80733 15.3375 2.92741 15.5482C3.04749 15.7588 3.22061 15.9344 3.42958 16.0574C3.63855 16.1804 3.87607 16.2466 4.11854 16.2493H15.8819C16.1244 16.2468 16.3621 16.1808 16.5712 16.0579C16.7803 15.9349 16.9536 15.7593 17.0737 15.5486C17.1938 15.3378 17.2567 15.0993 17.2559 14.8567C17.2551 14.6141 17.1908 14.376 17.0694 14.166L11.1885 4.34682C11.0648 4.1426 10.8906 3.97372 10.6826 3.85652C10.4745 3.73931 10.2398 3.67773 10.001 3.67773C9.76228 3.67773 9.52755 3.73931 9.31953 3.85652C9.11151 3.97372 8.93724 4.1426 8.81354 4.34682"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <p>
            Atenção! A soma dos percentuais lançados{" "}
            <strong>DEVE SER IGUAL a 100%</strong>.
          </p>
        </h5>
      </div>

      <AccordionModal title="Exemplos:">
        <div className="example">
          <img src={salesIcon} alt="ícone de vendas" />
          <div>
            <BaSeParagraph color="#000">
              <span>Distribuição de vendas</span>
            </BaSeParagraph>
            <ul className="example-list">
              <li>50% das vendas são pagas à vista.</li>
              <li>30% das vendas são recebidas em 30 dias.</li>
              <li>20% das vendas são recebidas em 60 dias.</li>
            </ul>
          </div>
        </div>
      </AccordionModal>

      <Deadlines
        selectDeadlines={selectDeadlines}
        clickItem={toggleDeadline}
        isDisabled={isDisabled}
      />

      <BaSeGrid>
        {[...selectDeadlines]
          .sort((a, b) => a - b)
          .map((order) => {
            const item = deadlines.find((d) => d.order === order) || {
              order,
              value: 0,
            };
            return (
              <BaSeGridItem key={order} desktop={{ size: 6 }}>
                <InputPercentageGraph
                  key={order}
                  name={`deadline_${order}`}
                  label={
                    order === 0
                      ? "Recebimento à vista"
                      : order === 1
                      ? "Recebimento em 30 dias"
                      : `Recebimento em ${order}x`
                  }
                  placeholder="0%"
                  suffix="%"
                  type="percentageGraph"
                  isDisabled={isDisabled}
                  maxValue={100}
                  maxLength={4}
                  background={colorsProgressBar[order]?.background}
                  barColor={colorsProgressBar[order]?.bar}
                  value={item.value}
                  onExternalChange={(e) => handleChange(order, e)}
                />
              </BaSeGridItem>
            );
          })}
      </BaSeGrid>

      <SumWatcher
        show={selectDeadlines.length > 0}
        totalSum={sum}
        deadlines={deadlines}
      />
    </div>
  );
};

export default ReceiptDeadline;
